import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarDays,
  CalendarOff,
  ChevronLeft,
  ChevronRight,
  List,
  Lock,
  Pencil,
  Plus,
  RotateCcw,
  Send,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  InlineAlert,
  LoadingState,
  PageHeader,
  StatusBadge,
  cn,
} from "../../shared/ui";
import type {
  PeriodTypeView,
  TimesheetDay,
  TimesheetStatus,
  TimesheetView,
  WorklogView,
} from "../../shared/types/api";
import { DecisionDrawer, RequestCorrectionDrawer } from "./TimesheetDrawers";
import { LogTimeDrawer } from "./LogTimeDrawer";
import { WeekGrid, WeekTotal } from "./WeekGrid";
import {
  REOPEN_STATUS_LABELS,
  REOPEN_STATUS_TONE,
  TIMESHEET_STATUS_LABELS,
  TIMESHEET_STATUS_TONE,
  formatDayLong,
  formatHoursAsTime,
  formatMinutes,
  formatPeriod,
  formatPeriodLabel,
  formatWeekRange,
  isoDate,
  shiftDays,
  shiftPeriod,
  weekBounds,
} from "./timesheetVocabulary";

/**
 * One person's period (§74), and everything §75 lets them do with it.
 *
 * <p>How long that period is belongs to the deployment, not to this screen: §72 allows weekly as
 * well as monthly submission, so the page asks the server which it is and navigates in that unit.
 * Nothing here computes a period boundary of its own — periods may not overlap, and a week that
 * begins a day early is not a slightly wrong period but one the database refuses outright.
 *
 * <p>The page is laid out day by day rather than as a grid of projects against dates. §74's own
 * example is a day at a time, and it is the shape that matches how somebody actually reconstructs
 * a week — "what did I do on Tuesday", not "how much went to Alpha this month".
 *
 * <p>Days with no time are shown rather than hidden. A period with three missing Tuesdays looks
 * complete if the empty rows are dropped, and the whole purpose of §79's expectation is to make
 * that gap visible.
 */
export function TimesheetPage() {
  const { timesheetId } = useParams();
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const queryClient = useQueryClient();

  const periodType = useQuery({
    queryKey: ["timesheet-period-type"],
    queryFn: () => api.get<PeriodTypeView>("/api/v1/timesheets/period-type"),
    // The cadence is a deployment setting, not something that changes while somebody is looking.
    staleTime: Infinity,
  });
  const period = periodType.data?.period ?? "MONTHLY";

  /* The day the reader is looking at. The server turns it into a period, so the screen and the
     database cannot disagree about where a period begins — which matters most in a weekly
     organisation, where a boundary a day out is a period the database will refuse. */
  const [focus, setFocus] = useState(() => isoDate(new Date()));
  /* Week for entering, the period for submitting (§72). Running monthly those differ — the month
     has the status and gets approved, the week is the rhythm somebody actually fills in. Running
     weekly they are the same seven days, and the grid below follows the period instead. */
  const [view, setView] = useState<"week" | "month">("week");
  const [week, setWeek] = useState(() => weekBounds(new Date()));
  const [logging, setLogging] = useState<{ date: string | null; entry: WorklogView | null } | null>(
    null,
  );
  const [correcting, setCorrecting] = useState(false);
  const [deciding, setDeciding] = useState<"approve" | "reject" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [locking, setLocking] = useState(false);

  // Two ways in: your own month, or a specific sheet an approver opened from the queue.
  const timesheet = useQuery({
    queryKey: timesheetId ? ["timesheet", timesheetId] : ["timesheet", "mine", focus, period],
    queryFn: () =>
      timesheetId
        ? api.get<TimesheetView>(`/api/v1/timesheets/${timesheetId}`)
        : api.post<TimesheetView>(`/api/v1/timesheets/period?on=${focus}`),
    enabled: timesheetId != null || periodType.isSuccess,
  });

  /* Running weekly, the grid shows the period itself rather than a week picked separately. Two
     week pickers on one screen, only one of which submits, is not something anybody works out
     by looking at it. */
  const gridWeek =
    period === "WEEKLY" && timesheet.data
      ? { from: timesheet.data.periodStart, to: timesheet.data.periodEnd }
      : week;

  /* Moving the grid moves the period too when they are the same thing, so the status badge above
     never describes a different week from the one on screen. */
  const showWeekOf = (date: Date) => {
    if (period === "WEEKLY") {
      setFocus(isoDate(date));
    } else {
      setWeek(weekBounds(date));
    }
  };

  const weekDays = useQuery({
    queryKey: ["timesheet-days", gridWeek.from, gridWeek.to, timesheetId ?? "mine"],
    queryFn: () =>
      api.get<TimesheetDay[]>(
        `/api/v1/timesheets/days?from=${gridWeek.from}&to=${gridWeek.to}`
        + (timesheet.data ? `&employeeId=${timesheet.data.employeeId}` : ""),
      ),
    // Waits for the timesheet so the range is fetched for the right person — a week for the
    // signed-in user would be the wrong answer on an approver's view of somebody else's sheet.
    enabled: view === "week" && timesheet.data != null,
  });

  const history = useQuery({
    queryKey: ["timesheets", "history"],
    queryFn: () => api.get<TimesheetView[]>("/api/v1/timesheets"),
    enabled: !timesheetId,
  });

  const sheet = timesheet.data;
  const mine = sheet != null && sheet.employeeId === user?.employeeId;

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["timesheet"] });
    void queryClient.invalidateQueries({ queryKey: ["timesheet-days"] });
    void queryClient.invalidateQueries({ queryKey: ["timesheets"] });
    // Logged hours are now real on work items, sprints and projects.
    void queryClient.invalidateQueries({ queryKey: ["work-item"] });
    void queryClient.invalidateQueries({ queryKey: ["sprint"] });
  };

  const submit = useMutation({
    mutationFn: () => api.post<TimesheetView>(`/api/v1/timesheets/${sheet?.id ?? ""}/submit`),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (caught: unknown) =>
      setError(caught instanceof ApiError ? caught.message : "Could not submit that period."),
  });

  const removeEntry = async (entry: WorklogView) => {
    setError(null);
    try {
      await api.del(`/api/v1/worklogs/${entry.id}`);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not remove that entry.");
    }
  };

  const summary = useMemo(() => byStatus(history.data ?? []), [history.data]);

  if (timesheet.isLoading) {
    return <LoadingState label="Opening your timesheet" />;
  }
  if (timesheet.isError || !sheet) {
    return <ErrorState error={timesheet.error} onRetry={() => void timesheet.refetch()} />;
  }

  const lockPeriod = async () => {
    if (!sheet.id) {
      return;
    }
    if (
      !window.confirm(
        "Lock this period? Afterwards it can only be changed by approving a correction request.",
      )
    ) {
      return;
    }
    setLocking(true);
    setError(null);
    try {
      await api.post(`/api/v1/timesheets/${sheet.id}/lock`);
      await queryClient.invalidateQueries({ queryKey: ["timesheet"] });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not lock the period.");
    } finally {
      setLocking(false);
    }
  };

  const expectation = sheet.expectation;
  const canDecide = can("TIMESHEET_APPROVE") && sheet.status === "SUBMITTED" && !mine;
  /*
   * §81: locking closes a period for good, once payroll or invoicing has taken the figure. Only
   * offered on an approved sheet somebody else owns — locking your own would let one person
   * settle their own hours beyond correction.
   */
  const canLock = can("TIMESHEET_APPROVE") && sheet.status === "APPROVED" && !mine && sheet.id;
  const pendingReopen = sheet.reopenRequests.find((request) => request.status === "PENDING");

  return (
    <div className="space-y-5">
      <PageHeader
        title={timesheetId ? `${sheet.employeeName}'s timesheet` : "My timesheet"}
        subtitle={formatPeriod(sheet.periodStart, sheet.periodEnd)}
        actions={
          <div className="flex items-center gap-2">
            {!timesheetId && (
              <div className="mr-1 flex items-center gap-1 rounded-lg border border-ink-200 p-0.5">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={period === "WEEKLY" ? "Previous week" : "Previous month"}
                  onClick={() => setFocus(isoDate(shiftPeriod(period, sheet.periodStart, -1)))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="min-w-[9.5rem] text-center text-sm font-medium text-ink-700">
                  {formatPeriodLabel(period, sheet.periodStart, sheet.periodEnd)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={period === "WEEKLY" ? "Next week" : "Next month"}
                  onClick={() => setFocus(isoDate(shiftPeriod(period, sheet.periodStart, 1)))}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
            {sheet.editable && mine && (
              <Button onClick={() => setLogging({ date: null, entry: null })}>
                <Plus className="h-4 w-4" /> Log time
              </Button>
            )}
            {sheet.editable && mine && (
              <Button
                variant="secondary"
                onClick={() => submit.mutate()}
                // Nothing logged means no period to submit — §75 refuses it, and offering the
                // button anyway would make the refusal look like a fault.
                disabled={submit.isPending || !sheet.id || !expectation.loggedHours}
              >
                <Send className="h-4 w-4" /> Submit
              </Button>
            )}
            {!sheet.editable && mine && sheet.id && sheet.status !== "SUBMITTED" && !pendingReopen && (
              <Button variant="secondary" onClick={() => setCorrecting(true)}>
                <RotateCcw className="h-4 w-4" /> Request a correction
              </Button>
            )}
            {canDecide && (
              <>
                <Button onClick={() => setDeciding("approve")}>Approve</Button>
                <Button variant="danger" onClick={() => setDeciding("reject")}>
                  Send back
                </Button>
              </>
            )}
            {canLock && (
              <Button
                variant="secondary"
                loading={locking}
                title="Closes the period. Afterwards only an approved correction request can change it."
                onClick={() => void lockPeriod()}
              >
                <Lock className="h-4 w-4" /> Lock
              </Button>
            )}
          </div>
        }
      />

      {error && <InlineAlert tone="error">{error}</InlineAlert>}

      {sheet.status === "REJECTED" && sheet.decisionNote && (
        <InlineAlert tone="warning">
          <span className="font-medium">Sent back by {sheet.decidedByName}:</span>{" "}
          {sheet.decisionNote}
        </InlineAlert>
      )}

      {pendingReopen && (
        <InlineAlert tone="info">
          A correction was requested on {new Date(pendingReopen.requestedAt).toLocaleDateString()}{" "}
          and is waiting for a decision. The period stays closed until it is approved.
        </InlineAlert>
      )}

      {expectation.partialPeriod && (
        <InlineAlert tone="info">
          This period is counted from {formatDayLong(expectation.effectiveFrom)} to{" "}
          {formatDayLong(expectation.effectiveTo)}, because employment dates cover only part of it.
        </InlineAlert>
      )}

      {/* §79's comparison, which is the row people actually read. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Figure label="Expected" value={formatHoursAsTime(expectation.expectedHours)} />
        <Figure label="Logged" value={formatHoursAsTime(expectation.loggedHours)} strong />
        <Figure label="Public holidays" value={formatHoursAsTime(expectation.holidayHours)} muted />
        <Figure label="Leave" value={formatHoursAsTime(expectation.leaveHours)} muted />
        {expectation.complete ? (
          <Figure
            label={(expectation.surplusHours ?? 0) > 0 ? "Beyond expected" : "Complete"}
            value={
              (expectation.surplusHours ?? 0) > 0
                ? formatHoursAsTime(expectation.surplusHours)
                : "—"
            }
            tone="green"
          />
        ) : (
          <Figure
            label="Still missing"
            value={formatHoursAsTime(expectation.missingHours)}
            tone="amber"
          />
        )}
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader
            title={view === "week" ? formatWeekRange(gridWeek.from, gridWeek.to) : "Day by day"}
            subtitle={
              view === "week"
                ? period === "WEEKLY"
                  ? "This week is the period: what you submit and what gets approved."
                  : "Enter time a week at a time. The month is what gets submitted."
                : "Every day of the period, empty days included."
            }
            action={
              <div className="flex items-center gap-2">
                {view === "week" && weekDays.data && <WeekTotal days={weekDays.data} />}
                <StatusBadge tone={TIMESHEET_STATUS_TONE[sheet.status]}>
                  {TIMESHEET_STATUS_LABELS[sheet.status]}
                </StatusBadge>
              </div>
            }
          />

          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-100 px-4 py-2">
            {/* Running weekly the header arrows already move this grid, because the grid is the
                period. A second pair here would be two controls with the same name doing the same
                thing — which a screen reader reads out as an ambiguity and a mouse user reads as
                a difference that must exist for some reason. */}
            <div className="flex items-center gap-1">
              {period !== "WEEKLY" && (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Previous week"
                  onClick={() => showWeekOf(shiftDays(gridWeek.from, -7))}
                  disabled={view !== "week"}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => showWeekOf(new Date())}
                disabled={view !== "week"}
              >
                Today
              </Button>
              {period !== "WEEKLY" && (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Next week"
                  onClick={() => showWeekOf(shiftDays(gridWeek.from, 7))}
                  disabled={view !== "week"}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Two ways of reading the same entries. The grid is for filling in; the list is for
                checking nothing was missed before submitting. Running weekly they cover the same
                seven days, so the second one is labelled for what it is rather than "Month". */}
            <div className="flex items-center rounded-lg border border-ink-200 p-0.5">
              <button
                type="button"
                onClick={() => setView("week")}
                className={cn(
                  "flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                  view === "week"
                    ? "bg-brand-50 text-brand-700"
                    : "text-ink-600 hover:text-ink-900",
                )}
              >
                <CalendarDays className="h-3.5 w-3.5" />
                Week
              </button>
              <button
                type="button"
                onClick={() => setView("month")}
                className={cn(
                  "flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                  view === "month"
                    ? "bg-brand-50 text-brand-700"
                    : "text-ink-600 hover:text-ink-900",
                )}
              >
                <List className="h-3.5 w-3.5" />
                {period === "WEEKLY" ? "List" : "Month"}
              </button>
            </div>
          </div>

          <CardBody className="p-0">
            {view === "week" ? (
              weekDays.isPending ? (
                <p className="px-4 py-10 text-center text-sm text-ink-500">Loading the week…</p>
              ) : weekDays.isError ? (
                <ErrorState
                  error={weekDays.error}
                  onRetry={() => void weekDays.refetch()}
                />
              ) : (
                <WeekGrid
                  days={weekDays.data}
                  editable={sheet.editable && mine}
                  onAdd={(date) => setLogging({ date, entry: null })}
                  onEdit={(entry) => setLogging({ date: entry.workDate, entry })}
                  onDelete={removeEntry}
                />
              )
            ) : (
              <ul className="divide-y divide-ink-100">
                {sheet.days.map((day) => (
                  <DayRow
                    key={day.date}
                    day={day}
                    editable={sheet.editable && mine}
                    onAdd={() => setLogging({ date: day.date, entry: null })}
                    onEdit={(entry) => setLogging({ date: day.date, entry })}
                    onDelete={removeEntry}
                  />
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <div className="space-y-5">
          <Card>
            <CardHeader title="Where the time went" />
            <CardBody>
              {sheet.byProject.length === 0 ? (
                <p className="text-sm text-ink-400">Nothing logged in this period yet.</p>
              ) : (
                <ul className="space-y-3">
                  {sheet.byProject.map((total) => (
                    <li key={total.projectId ?? "internal"}>
                      <div className="flex items-baseline justify-between gap-3 text-sm">
                        <span className="truncate text-ink-800">
                          {total.projectKey && (
                            <span className="mr-1.5 font-mono text-xs text-ink-500">
                              {total.projectKey}
                            </span>
                          )}
                          {total.projectName}
                        </span>
                        <span className="tnum shrink-0 font-medium text-ink-900">
                          {formatMinutes(total.minutes)}
                        </span>
                      </div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-ink-100">
                        <div
                          className="h-full rounded-full bg-brand-500"
                          style={{ width: `${Math.round(total.share * 100)}%` }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          {(sheet.submittedAt || sheet.decidedAt || sheet.reopenRequests.length > 0) && (
            <Card>
              <CardHeader title="What has happened to this period" />
              <CardBody className="space-y-3 text-sm">
                {sheet.submittedAt && (
                  <Event
                    when={sheet.submittedAt}
                    what={`Submitted by ${sheet.submittedByName ?? "—"}`}
                  />
                )}
                {sheet.decidedAt && (
                  <Event
                    when={sheet.decidedAt}
                    what={`${sheet.status === "REJECTED" ? "Sent back" : "Approved"} by ${
                      sheet.decidedByName ?? "—"
                    }`}
                    note={sheet.decisionNote}
                  />
                )}
                {sheet.lockedAt && <Event when={sheet.lockedAt} what="Locked" />}
                {/* §153's correction history: every reopen, decided or not. */}
                {sheet.reopenRequests.map((request) => (
                  <div key={request.id} className="rounded-lg border border-ink-200 p-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-ink-500">
                        {new Date(request.requestedAt).toLocaleString()} ·{" "}
                        {request.requestedByName}
                      </span>
                      <StatusBadge tone={REOPEN_STATUS_TONE[request.status]}>
                        {REOPEN_STATUS_LABELS[request.status]}
                      </StatusBadge>
                    </div>
                    <p className="mt-1 text-ink-800">{request.reason}</p>
                    {request.decisionNote && (
                      <p className="mt-1 text-xs text-ink-500">
                        {request.decidedByName}: {request.decisionNote}
                      </p>
                    )}
                  </div>
                ))}
              </CardBody>
            </Card>
          )}

          {/* §80: the person's own standing, across every period rather than just this one. */}
          {!timesheetId && history.data && history.data.length > 0 && (
            <Card>
              <CardHeader title="Your periods" />
              <CardBody className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <Tally label="Draft" value={summary.DRAFT} />
                  <Tally label="Waiting" value={summary.SUBMITTED} />
                  <Tally label="Approved" value={summary.APPROVED} />
                  <Tally label="Sent back" value={summary.REJECTED} />
                </div>
                <ul className="divide-y divide-ink-100 border-t border-ink-100">
                  {history.data.map((entry) => (
                    <li key={entry.id}>
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-2 py-2 text-left text-sm hover:text-brand-600"
                        onClick={() => {
                          setFocus(entry.periodStart);
                          navigate("/timesheets");
                        }}
                      >
                        <span className="text-ink-700">
                          {formatPeriodLabel(period, entry.periodStart, entry.periodEnd)}
                        </span>
                        <span className="flex items-center gap-2">
                          <span className="tnum text-ink-500">
                            {formatHoursAsTime(entry.expectation.loggedHours)}
                          </span>
                          <StatusBadge tone={TIMESHEET_STATUS_TONE[entry.status]}>
                            {TIMESHEET_STATUS_LABELS[entry.status]}
                          </StatusBadge>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          )}
        </div>
      </div>

      <LogTimeDrawer
        open={logging != null}
        date={logging?.date ?? null}
        entry={logging?.entry ?? null}
        onClose={() => setLogging(null)}
        onSaved={refresh}
      />
      <RequestCorrectionDrawer
        open={correcting}
        timesheet={sheet}
        onClose={() => setCorrecting(false)}
        onSaved={refresh}
      />
      <DecisionDrawer
        open={deciding != null}
        decision={deciding ?? "approve"}
        timesheet={sheet}
        onClose={() => setDeciding(null)}
        onSaved={refresh}
      />
    </div>
  );
}

/** Logged hours grouped by what state the period is in — §80's draft / submitted / approved. */
function byStatus(periods: TimesheetView[]): Record<TimesheetStatus, number> {
  const totals: Record<TimesheetStatus, number> = {
    DRAFT: 0,
    SUBMITTED: 0,
    APPROVED: 0,
    REJECTED: 0,
    LOCKED: 0,
  };
  for (const period of periods) {
    totals[period.status] += period.expectation.loggedHours ?? 0;
  }
  return totals;
}

function DayRow({
  day,
  editable,
  onAdd,
  onEdit,
  onDelete,
}: {
  day: TimesheetDay;
  editable: boolean;
  onAdd: () => void;
  onEdit: (entry: WorklogView) => void;
  onDelete: (entry: WorklogView) => void;
}) {
  const nonWorking = !day.workingDay || day.holiday;
  const short = day.workingDay && !day.holiday && day.loggedMinutes < day.expectedMinutes;

  return (
    <li className={nonWorking && day.entries.length === 0 ? "bg-ink-50/40" : undefined}>
      <div className="flex items-center justify-between gap-3 px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          {/* A non-working day is quieter, not unreadable: ink-400 dropped it to 3:1 against the
              page, and the date is the row's label rather than decoration. */}
          <span
            className={
              nonWorking ? "text-sm text-ink-500" : "text-sm font-medium text-ink-800"
            }
          >
            {formatDayLong(day.date)}
          </span>
          {day.holiday && (
            <StatusBadge tone="blue">Public holiday</StatusBadge>
          )}
          {day.onLeave && (
            <span className="flex items-center gap-1 text-xs text-ink-500">
              <CalendarOff className="h-3 w-3" /> On leave
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span
            className={
              "tnum text-sm " +
              (day.loggedMinutes === 0
                ? "text-ink-400"
                : short
                  ? "text-amber-700"
                  : "font-medium text-ink-900")
            }
          >
            {day.loggedMinutes === 0 ? "—" : formatMinutes(day.loggedMinutes)}
          </span>
          {day.expectedMinutes > 0 && (
            <span className="tnum w-16 text-right text-xs text-ink-500">
              of {formatMinutes(day.expectedMinutes)}
            </span>
          )}
          {editable && (
            <Button variant="ghost" size="sm" aria-label={`Log time on ${day.date}`} onClick={onAdd}>
              <Plus className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {day.entries.length > 0 && (
        <ul className="space-y-1 px-4 pb-2.5">
          {day.entries.map((entry) => (
            <li
              key={entry.id}
              className="flex items-center justify-between gap-3 rounded-lg bg-ink-50/70 px-3 py-2"
            >
              <div className="min-w-0">
                <p className="truncate text-sm text-ink-800">
                  {entry.workItemKey ? (
                    <>
                      <span className="mr-1.5 font-mono text-xs text-ink-500">
                        {entry.workItemKey}
                      </span>
                      {entry.workItemTitle}
                    </>
                  ) : (
                    (entry.projectName ?? "Internal")
                  )}
                </p>
                {entry.description && (
                  <p className="truncate text-xs text-ink-500">{entry.description}</p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <span className="tnum mr-1 text-sm text-ink-700">
                  {formatMinutes(entry.minutes)}
                </span>
                {editable && entry.editable && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Correct this entry"
                      onClick={() => onEdit(entry)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Remove this entry"
                      onClick={() => onDelete(entry)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function Figure({
  label,
  value,
  strong,
  muted,
  tone,
}: {
  label: string;
  value: string;
  strong?: boolean;
  muted?: boolean;
  tone?: "green" | "amber";
}) {
  const colour =
    tone === "green"
      ? "text-emerald-700"
      : tone === "amber"
        ? "text-amber-700"
        : muted
          ? "text-ink-500"
          : "text-ink-900";
  return (
    <div className="rounded-xl border border-ink-200 bg-surface px-4 py-3">
      <p className="text-xs text-ink-500">{label}</p>
      <p className={`tnum mt-1 ${strong ? "text-2xl" : "text-xl"} font-semibold ${colour}`}>
        {value}
      </p>
    </div>
  );
}

function Tally({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg bg-ink-50 px-3 py-2">
      {/* ink-500 on the tinted panel lands at 4.4:1 — just under AA, and free to fix. */}
      <p className="text-xs text-ink-600">{label}</p>
      <p className="tnum text-sm font-medium text-ink-900">{formatHoursAsTime(value)}</p>
    </div>
  );
}

function Event({ when, what, note }: { when: string; what: string; note?: string | null }) {
  return (
    <div>
      <p className="text-ink-800">{what}</p>
      <p className="text-xs text-ink-500">{new Date(when).toLocaleString()}</p>
      {note && <p className="mt-0.5 text-xs text-ink-600">{note}</p>}
    </div>
  );
}
